import { report } from "./telemetry";
import { keyboardIsUp } from "./soft-keyboard.ts";
import { coarsePointer } from "./press-word.ts";

/**
 * Keeps the window pinned at the top on iOS.
 *
 * The document never scrolls (each screen's content pane does), but iOS can
 * still move the window: the keyboard scrolls it to lift a focused field and
 * does not always scroll back, and a home-screen app can relaunch with the
 * previous session's window offset restored. Either way the whole layout
 * sits shifted up with an empty band under the tab bar, and nothing in the
 * page can undo it by hand. So the window is put back at the top at launch,
 * after a field loses focus, when the visual viewport changes size, and when
 * the app returns to the foreground.
 *
 * Every launch also logs the viewport geometry once, and every reset that
 * actually moved something logs what it found, so the cause can be read off
 * the workshop's Device log.
 */
export function installViewportGuard(): void {
  try {
    history.scrollRestoration = "manual";
  } catch {
    /* not supported */
  }

  // The top safe inset as iOS resolves it, read from a probe because env() is not resolvable
  // through a custom property.
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;padding-top:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  const safeTop = () => parseFloat(getComputedStyle(probe).paddingTop) || 0;

  // iOS home-screen apps with a translucent status bar can report a layout viewport that stops
  // one status bar short of the screen while drawing the page full-screen under the bar. Anything
  // fixed to the viewport bottom then floats above a band of bare page background. The gap is
  // published as --vp-gap so the tab bar, the corner buttons and the scroller can reach the
  // real bottom. It is only trusted when the page is in fact drawn under the status bar (the top
  // inset is non-zero) and the shortfall matches that inset.
  const applyGap = () => {
    const inset = safeTop();
    const shortfall = window.screen.height - window.innerHeight;
    const portrait = window.innerHeight >= window.innerWidth;
    const gap = inset > 0 && portrait && shortfall > 0 && shortfall <= inset + 2 ? shortfall : 0;
    document.documentElement.style.setProperty("--vp-gap", `${gap}px`);
    return gap;
  };

  const geometry = () => ({
    scrollY: Math.round(window.scrollY),
    innerHeight: window.innerHeight,
    screenHeight: window.screen.height,
    vvHeight: window.visualViewport ? Math.round(window.visualViewport.height) : null,
    vvOffsetTop: window.visualViewport ? Math.round(window.visualViewport.offsetTop) : null,
    docHeight: document.documentElement.scrollHeight,
    safeTop: safeTop(),
    gap: applyGap(),
    standalone: window.matchMedia("(display-mode: standalone)").matches,
  });

  // While a field has focus the keyboard is up and iOS is moving the viewport on purpose to keep
  // the field in view; fighting that makes scrolling flicker back. Leave it alone until blur.
  const fieldFocused = () => {
    const el = document.activeElement;
    return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || (el as HTMLElement).isContentEditable);
  };

  let lastReport = 0;
  const reset = (why: string) => {
    if (fieldFocused()) return;
    const before = geometry();
    if (before.scrollY === 0 && window.scrollX === 0) return;
    window.scrollTo(0, 0);
    if (Date.now() - lastReport > 5000) {
      lastReport = Date.now();
      report("viewport.reset", why, { ...before, after: Math.round(window.scrollY) });
    }
  };

  // Launch: iOS may restore an old offset after the first paint, so check more than once.
  applyGap();
  reset("launch");
  for (const ms of [100, 600, 1500]) setTimeout(() => { applyGap(); reset(`launch+${ms}`); }, ms);
  setTimeout(() => report("viewport.launch", "", geometry()), 1500);

  window.addEventListener("pageshow", () => reset("pageshow"));
  window.addEventListener("resize", () => applyGap());
  // After the keyboard closes (blur), give iOS a moment to settle, then put the window back.
  document.addEventListener("focusout", () => setTimeout(() => reset("focusout"), 250), true);
  window.visualViewport?.addEventListener("resize", () => setTimeout(() => { applyGap(); reset("vv-resize"); }, 250));
  window.addEventListener("orientationchange", () => setTimeout(() => { applyGap(); reset("orientation"); }, 300));

  // While the keyboard is up, iOS pans the whole window to lift the focused field, which slides the
  // top bar (and the peon in it) off the top of the screen. Instead of fighting the pan, the app is
  // pinned to the visible part of the screen: html.keyboard-up sizes it to the visual viewport's
  // height and moves it down by however far the window has been panned, so the top bar stays at the
  // visible top and the content pane above the keyboard gets shorter and scrolls instead.
  // Independently of any geometry: from the moment a field takes focus until it loses it, the
  // controls fixed to the bottom of the screen are hidden on touch devices (styles.css
  // html.field-focused). The keyboard covers them anyway, and hiding them before iOS starts moving
  // viewports around is the one way to be sure they are never seen sliding up over the keyboard.
  const root = document.documentElement;
  const syncFieldFocus = () => root.classList.toggle("field-focused", fieldFocused());
  document.addEventListener("focusin", syncFieldFocus, true);
  document.addEventListener("focusout", () => setTimeout(syncFieldFocus, 0), true);

  const vv = window.visualViewport;
  if (vv) {
    let keyboardUp = false;
    let reported = false;
    const syncKeyboard = () => {
      try {
        const up = keyboardIsUp({ coarse: coarsePointer(), fieldFocused: fieldFocused(), vvHeight: vv.height, innerHeight: window.innerHeight });
        if (up) {
          root.style.setProperty("--kb-vh", `${Math.round(vv.height)}px`);
          root.style.setProperty("--kb-top", `${Math.round(vv.pageTop)}px`);
          // The full window height and the visual viewport's offset in it, so anything fixed to the
          // window's bottom (the tab bar, the corner button) can stay at the real bottom of the
          // screen, under the keyboard, instead of riding up above it with the pinned app.
          root.style.setProperty("--kb-inner", `${window.innerHeight}px`);
          root.style.setProperty("--kb-offset", `${Math.round(vv.offsetTop)}px`);
          if (!keyboardUp) {
            keyboardUp = true;
            root.classList.add("keyboard-up");
            // The content pane just got shorter: bring the field back into view inside it.
            const el = document.activeElement as HTMLElement | null;
            setTimeout(() => el?.scrollIntoView?.({ block: "nearest" }), 0);
          }
          if (!reported) {
            reported = true;
            // Where the bottom controls actually ended up (layout-viewport coordinates), a moment
            // after the pinning applied, so a report can be read against the visual viewport.
            setTimeout(() => {
              const at = (selector: string) => {
                const r = document.querySelector(selector)?.getBoundingClientRect();
                return r ? { top: Math.round(r.top), bottom: Math.round(r.bottom) } : null;
              };
              report("viewport.keyboard", "", { ...geometry(), vvPageTop: Math.round(vv.pageTop), tabbar: at(".tabbar"), fab: at(".fab") });
            }, 300);
          }
        } else if (keyboardUp) {
          keyboardUp = false;
          reported = false;
          root.classList.remove("keyboard-up");
          root.style.removeProperty("--kb-vh");
          root.style.removeProperty("--kb-top");
          root.style.removeProperty("--kb-inner");
          root.style.removeProperty("--kb-offset");
        }
      } catch {
        /* never let viewport bookkeeping break the page */
      }
    };
    vv.addEventListener("resize", syncKeyboard);
    vv.addEventListener("scroll", syncKeyboard);
    document.addEventListener("focusin", () => setTimeout(syncKeyboard, 50), true);
    document.addEventListener("focusout", () => setTimeout(syncKeyboard, 50), true);
  }
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") reset("visible");
  });
}
